/**
 * @file     funnelTracker.ts
 * @readme   ./CLAUDE.md
 * @purpose  Lightweight client tracker for deal-finder funnel events. Stable per-tab session id (sessionStorage), debounced batched POST to /api/deal-finder/track, fire-and-forget so it never blocks the user. Designed to be invoked imperatively from step components: trackStepViewed('intent'), trackOptionSelected('intent', 'buy-home'), trackFieldBlurred('email', email.length), trackSubmit('attempted' | 'succeeded' | 'failed').
 */

const SESSION_STORAGE_KEY = 'relta-deal-finder-session-id';
const BATCH_FLUSH_MS = 1500;

export type FunnelStepKey =
  | 'intent'
  | 'property_type'
  | 'amenities'
  | 'budget'
  | 'location'
  | 'timeline'
  | 'cadence'
  | 'presale_use_case'
  | 'deposit_readiness'
  | 'size_beds_baths'
  | 'beds_baths'
  | 'square_footage'
  | 'location_parent'
  | 'location_sub_zones'
  | 'layout'
  | 'building_concerns'
  | 'viewing_style'
  | 'criteria_review'
  | 'lead_capture'
  | 'confirmation'
  | 'address'
  | 'entry'
  | 'photos';

/**
 * Map a DealFlowStepName (camelCase, used by the engine) to the canonical
 * snake_case step key the analytics table expects. Returns null for steps we
 * do not track (e.g. intermediate 'results' page).
 */
export function dealFlowStepNameToFunnelKey(name: string): FunnelStepKey | null {
  // Per-region sub-zone steps are encoded as 'locationSubZones:<slug>' in the
  // expanded stepOrder. Strip the suffix so all per-region screens log under
  // the single 'location_sub_zones' analytics key.
  const baseName = name.includes(':') ? name.slice(0, name.indexOf(':')) : name;
  switch (baseName) {
    case 'intent': return 'intent';
    case 'propertyType': return 'property_type';
    case 'amenities': return 'amenities';
    case 'budget': return 'budget';
    case 'location': return 'location';
    case 'timeline': return 'timeline';
    case 'cadence': return 'cadence';
    case 'presaleUse': return 'presale_use_case';
    case 'depositReadiness': return 'deposit_readiness';
    case 'sizeBedsBaths': return 'size_beds_baths';
    case 'bedsBaths': return 'beds_baths';
    case 'squareFootage': return 'square_footage';
    case 'locationParent': return 'location_parent';
    case 'locationSubZones': return 'location_sub_zones';
    case 'layout': return 'layout';
    case 'buildingConcerns': return 'building_concerns';
    case 'viewingStyle': return 'viewing_style';
    case 'criteriaReview': return 'criteria_review';
    case 'leadCapture': return 'lead_capture';
    case 'confirmation': return 'confirmation';
    default: return null;
  }
}

export type FunnelEventType =
  | 'step_viewed'
  | 'option_selected'
  | 'option_deselected'
  | 'field_focused'
  | 'field_blurred'
  | 'step_completed'
  | 'step_back'
  | 'submit_attempted'
  | 'submit_succeeded'
  | 'submit_failed'
  // PR 3: criteria_review preview-step events
  | 'chip_clicked'
  | 'expand_budget'
  | 'adjust_manually'
  | 'skip_preview';

/**
 * Attribution context, set once per funnel mount so every event in this session
 * can be filtered/grouped by which landing variant (and which UTM-derived intent)
 * drove the visitor. Values are stable for the duration of the funnel session;
 * a return visitor opening the funnel from a different ad gets a fresh context.
 */
export type FunnelAttribution = {
  intent: string;
  variant_id: string;
  persona?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  flow_id?: string;
};

type Event = {
  session_id: string;
  step_key: FunnelStepKey;
  event_type: FunnelEventType;
  field_name?: string;
  chars?: number;
  selected_value?: string;
  time_on_step_ms?: number;
  payload?: Record<string, unknown>;
  attribution?: FunnelAttribution;
};

const queue: Event[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const stepEnteredAt = new Map<FunnelStepKey, number>();
let currentAttribution: FunnelAttribution | null = null;

/**
 * Set (or replace) the attribution context for all subsequent funnel events.
 * Idempotent, safe to call multiple times during a mount; latest call wins.
 * Pass null to clear (used by tests).
 */
export function setFunnelAttribution(ctx: FunnelAttribution | null): void {
  currentAttribution = ctx;
}

/** Read the current attribution context. Exposed for tests + introspection. */
export function getFunnelAttribution(): FunnelAttribution | null {
  return currentAttribution;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId(): string {
  if (typeof window === 'undefined') return 'ssr';
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = generateId();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage unavailable (private browsing); fall back to ephemeral id.
    return generateId();
  }
}

async function flush(): Promise<void> {
  flushTimer = null;
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
    await secureFetch('/api/deal-finder/track', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch {
    // Best-effort. If this fails the user never knows.
  }
}

function enqueue(partial: Omit<Event, 'session_id' | 'attribution'>): void {
  if (typeof window === 'undefined') return;
  const event: Event = {
    ...partial,
    session_id: getSessionId(),
    ...(currentAttribution ? { attribution: currentAttribution } : {}),
  };
  queue.push(event);
  // Mirror to Relta analytics_events. Old table keeps writing during
  // backfill + dual-write window so admin funnel queries are unaffected.
  void mirrorToRelta(event);
  if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      void flush();
    }, BATCH_FLUSH_MS);
  }
}

async function mirrorToRelta(event: Event): Promise<void> {
  try {
    const { reltaAnalytics } = await import('@/services/analytics/relta/client');
    if (event.event_type === 'step_viewed') {
      reltaAnalytics.track('deal_finder.step.view', {
        step: event.step_key,
        session_id: event.session_id,
      });
    } else if (event.event_type === 'option_selected' && event.selected_value) {
      reltaAnalytics.track('deal_finder.option.select', {
        step: event.step_key,
        option: event.selected_value,
      });
    } else if (event.event_type === 'field_blurred' && event.field_name) {
      reltaAnalytics.track('deal_finder.field.blur', {
        field: event.field_name,
        has_value: (event.chars ?? 0) > 0,
      });
    } else if (event.event_type === 'submit_succeeded') {
      reltaAnalytics.track('deal_finder.submit', { success: true });
      const a = event.attribution;
      reltaAnalytics.track('deal_finder.complete', {
        intent: a?.intent,
        location: a?.utm_source,
      });
    } else if (event.event_type === 'submit_failed') {
      reltaAnalytics.track('deal_finder.submit', { success: false });
    }
  } catch {
    // Mirror is best-effort.
  }
}

/** Force-flush the queue right now. Useful before page unload or navigation. */
export function flushFunnelTracker(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
  }
  void flush();
}

if (typeof window !== 'undefined') {
  // Best-effort flush on page hide so we don't lose the last few events.
  window.addEventListener('pagehide', () => {
    flushFunnelTracker();
  });
  window.addEventListener('beforeunload', () => {
    flushFunnelTracker();
  });
}

export function trackStepViewed(stepKey: FunnelStepKey): void {
  stepEnteredAt.set(stepKey, performance.now());
  enqueue({ step_key: stepKey, event_type: 'step_viewed' });
}

export function trackStepCompleted(stepKey: FunnelStepKey): void {
  const enteredAt = stepEnteredAt.get(stepKey);
  const time_on_step_ms = enteredAt !== undefined ? Math.round(performance.now() - enteredAt) : undefined;
  enqueue({ step_key: stepKey, event_type: 'step_completed', time_on_step_ms });
}

export function trackStepBack(stepKey: FunnelStepKey): void {
  enqueue({ step_key: stepKey, event_type: 'step_back' });
}

export function trackOptionSelected(stepKey: FunnelStepKey, value: string): void {
  enqueue({ step_key: stepKey, event_type: 'option_selected', selected_value: value });
}

export function trackOptionDeselected(stepKey: FunnelStepKey, value: string): void {
  enqueue({ step_key: stepKey, event_type: 'option_deselected', selected_value: value });
}

export function trackFieldFocused(stepKey: FunnelStepKey, fieldName: string): void {
  enqueue({ step_key: stepKey, event_type: 'field_focused', field_name: fieldName });
}

export function trackFieldBlurred(stepKey: FunnelStepKey, fieldName: string, chars: number): void {
  enqueue({
    step_key: stepKey,
    event_type: 'field_blurred',
    field_name: fieldName,
    chars: Math.max(0, Math.min(5000, Math.floor(chars))),
  });
}

export function trackSubmit(
  outcome: 'attempted' | 'succeeded' | 'failed',
  payload?: Record<string, unknown>,
): void {
  const event_type: FunnelEventType =
    outcome === 'attempted' ? 'submit_attempted'
    : outcome === 'succeeded' ? 'submit_succeeded'
    : 'submit_failed';
  enqueue({ step_key: 'lead_capture', event_type, payload });
  // Submit outcomes are high-priority signals; flush right away.
  flushFunnelTracker();
}

// ─── PR 3: criteria_review preview-step trackers ─────────────────────────

/** Fires step_viewed with extra payload (matching_count, price_p50). */
export function trackCriteriaReviewViewed(payload: { matchingCount: number; priceP50: number | null }): void {
  stepEnteredAt.set('criteria_review', performance.now());
  enqueue({
    step_key: 'criteria_review',
    event_type: 'step_viewed',
    payload: { matching_count: payload.matchingCount, price_p50: payload.priceP50 },
  });
}

export function trackChipClicked(field: string): void {
  enqueue({ step_key: 'criteria_review', event_type: 'chip_clicked', payload: { field } });
}

export function trackExpandBudget(from: string | undefined, to: string): void {
  enqueue({ step_key: 'criteria_review', event_type: 'expand_budget', payload: { from: from ?? null, to } });
}

export function trackAdjustManually(): void {
  enqueue({ step_key: 'criteria_review', event_type: 'adjust_manually' });
}

export function trackSkipPreview(): void {
  enqueue({ step_key: 'criteria_review', event_type: 'skip_preview' });
}

